import React, { useMemo, useState } from 'react';

import cn from 'classnames';
import { lowerCase } from 'lodash';
import {
  MdAddCircle,
  MdNewReleases,
  MdOutlineRemoveCircle,
  MdSearch,
} from 'react-icons/md';
import { FormattedMessage } from 'react-intl';

import { OrgPositionDetail } from '@appTypes/esb/organizations/positions/_orgGuid';
import { CelKey, PanelTitle } from '@modules/permissionManager/constants';
import { useUnitRosterAdultsWithPositions } from '@modules/permissionManager/duck/hooks';
import { intl, unitTypes } from '@modules/shared';
import {
  Button,
  Checkbox,
  Input,
  PersonAvatar,
  S,
  Table,
} from '@modules/shared/components';

import styles from '../permissionsTab.less';
import { PermsConfigurationObject, RosterAdultMemberExtended } from '../utils';

interface PermissionsPanelProps {
  panelType: PanelTitle;
}

export const PermissionsPanel = ({ panelType }: PermissionsPanelProps) => {
  const [searchTxt, setSearchTxt] = useState<string>('');
  const {
    data: tableData,
    loading,
    unitType,
  } = useUnitRosterAdultsWithPositions();

  const filteredTableData = useMemo(() => {
    if (searchTxt) {
      return tableData.filter(({ personFullName }) =>
        lowerCase(personFullName).includes(lowerCase(searchTxt)),
      );
    }

    return tableData;
  }, [tableData, searchTxt]);

  const { unitLabel, subUnitLabel } = useMemo(() => {
    if (unitType === unitTypes.TROOP)
      return { unitLabel: 'Troop', subUnitLabel: 'Patrol' };
    if (unitType === unitTypes.PACK)
      return { unitLabel: 'Pack', subUnitLabel: 'Den' };
    return { unitLabel: 'Unit', subUnitLabel: 'Subunit' };
  }, [unitType]);

  //   TODO: If the logic gets crazy move this to its own component
  const getCell = (positions: OrgPositionDetail[], celKey: CelKey) => {
    const celPerms = positions.reduce((acc, pos) => {
      const celPerm =
        // @ts-expect-error TODO: PermsConfigurationObject type
        PermsConfigurationObject[pos.positionLong]?.[celKey];

      return { ...acc, ...celPerm };
    }, {});

    if (!celPerms) {
      return null;
    }

    if (celPerms.isUnitRequired) {
      return (
        <div className={styles.chkbxContainer}>
          <Checkbox disabled checked />
        </div>
      );
    }

    if (celPerms.isRecommended) {
      return (
        <div className={styles.chkbxContainer}>
          <Checkbox />
          <MdNewReleases className={styles.icon} />
        </div>
      );
    }

    if (
      celPerms.isUnitEnabled &&
      !celPerms.isSubRequired &&
      !celPerms.isSubEnabled
    ) {
      return (
        <div className={styles.chkbxContainer}>
          <Checkbox />
        </div>
      );
    }

    if (celPerms.isSubRequired || celPerms.isSubEnabled) {
      return (
        <div className={cn(styles.chkbxContainer)}>
          <div>
            <S>{subUnitLabel}</S>
            <Checkbox
              disabled={celPerms.isSubRequired}
              checked={celPerms.isSubRequired}
            />
          </div>
          {celPerms.isUnitEnabled && (
            <div className={styles.secondaryChcbx}>
              <S>{unitLabel}</S>
              <Checkbox />
            </div>
          )}
        </div>
      );
    }

    if (celPerms.cannotAssign) {
      return (
        <div className={styles.chkbxContainer}>
          <S size="5" bold>
            Can Not Assign
          </S>
        </div>
      );
    }

    return null;
  };

  const getProgramSpecificUnitColumns = () => {
    if (unitType === unitTypes.PACK) {
      return [
        {
          title: intl.formatMessage({
            id: 'permissionManager.tabTitle.denCreate',
          }),
          dataIndex: 'denCreate',
          key: 'denCreate',
          width: '9rem',
          render(_: unknown, data: RosterAdultMemberExtended) {
            return getCell(data.userOrgPositions, CelKey.SUBUNIT_CREATE);
          },
        },
        {
          title: intl.formatMessage({
            id: 'permissionManager.tabTitle.denEdit',
          }),
          dataIndex: 'denEdit',
          key: 'denEdit',
          width: '9rem',
          render(_: unknown, data: RosterAdultMemberExtended) {
            return getCell(data.userOrgPositions, CelKey.SUBUNIT_EDIT);
          },
        },
        {
          title: intl.formatMessage({
            id: 'permissionManager.tabTitle.denChiefAdd',
          }),
          dataIndex: 'denChiefAdd',
          key: 'denChiefAdd',
          width: '9rem',
          render(_: unknown, data: RosterAdultMemberExtended) {
            return getCell(data.userOrgPositions, CelKey.SUBUNIT_DENCHIEF_ADD);
          },
        },
      ];
    }

    if (unitType === unitTypes.TROOP) {
      return [
        {
          title: intl.formatMessage({
            id: 'permissionManager.tabTitle.patrolCreate',
          }),
          dataIndex: 'patrolCreate',
          key: 'patrolCreate',
          width: '9rem',
          render(_: unknown, data: RosterAdultMemberExtended) {
            return getCell(data.userOrgPositions, CelKey.SUBUNIT_CREATE);
          },
        },
        {
          title: intl.formatMessage({
            id: 'permissionManager.tabTitle.patrolEdit',
          }),
          dataIndex: 'patrolEdit',
          key: 'patrolEdit',
          width: '9rem',
          render(_: unknown, data: RosterAdultMemberExtended) {
            return getCell(data.userOrgPositions, CelKey.SUBUNIT_EDIT);
          },
        },
      ];
    }

    return [];
  };

  const getProgramSpecificAdvancementColumns = () => {
    if (unitType === unitTypes.TROOP) {
      return [
        {
          title: intl.formatMessage({
            id: 'permissionManager.tabTitle.meritBadgeList',
          }),
          dataIndex: 'mbList',
          key: 'mbList',
          width: '9rem',
          render(_: unknown, data: RosterAdultMemberExtended) {
            return getCell(data.userOrgPositions, CelKey.ADVANCEMENT_MBLIST);
          },
        },
        {
          title: intl.formatMessage({
            id: 'permissionManager.tabTitle.scoutEditor',
          }),
          dataIndex: 'scoutEditor',
          key: 'scoutEditor',
          width: '9rem',
          render(_: unknown, data: RosterAdultMemberExtended) {
            return getCell(
              data.userOrgPositions,
              CelKey.ADVANCEMENT_SCOUT_EDITOR,
            );
          },
        },
        {
          title: intl.formatMessage({
            id: 'permissionManager.tabTitle.tenderfootEditor',
          }),
          dataIndex: 'tenderfootEditor',
          key: 'tenderfootEditor',
          width: '9rem',
          render(_: unknown, data: RosterAdultMemberExtended) {
            return getCell(
              data.userOrgPositions,
              CelKey.ADVANCEMENT_TENDERFOOT_EDITOR,
            );
          },
        },
        {
          title: intl.formatMessage({
            id: 'permissionManager.tabTitle.secondClassEditor',
          }),
          dataIndex: 'secondClassEditor',
          key: 'secondClassEditor',
          width: '9rem',
          render(_: unknown, data: RosterAdultMemberExtended) {
            return getCell(
              data.userOrgPositions,
              CelKey.ADVANCEMENT_SECONDCLASS_EDITOR,
            );
          },
        },
        {
          title: intl.formatMessage({
            id: 'permissionManager.tabTitle.firstClassEditor',
          }),
          dataIndex: 'firstClassEditor',
          key: 'firstClassEditor',
          width: '9rem',
          render(_: unknown, data: RosterAdultMemberExtended) {
            return getCell(
              data.userOrgPositions,
              CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR,
            );
          },
        },
      ];
    }

    return [];
  };

  const getColumns = (type: PanelTitle) => {
    switch (type) {
      case PanelTitle.ACTIVITY:
        return [
          {
            title: intl.formatMessage({
              id: 'permissionManager.tabTitle.activityCreate',
            }),
            dataIndex: 'create',
            key: 'create',
            width: '9rem',
            render(_: unknown, data: RosterAdultMemberExtended) {
              return getCell(data.userOrgPositions, CelKey.ACTIVITY_CREATE);
            },
          },
        ];
      case PanelTitle.ADVANCEMENT:
        return [
          {
            title: intl.formatMessage({
              id: 'permissionManager.tabTitle.advancementAward',
            }),
            dataIndex: 'award',
            key: 'award',
            width: '9rem',
            render(_: unknown, data: RosterAdultMemberExtended) {
              return getCell(data.userOrgPositions, CelKey.ADVANCEMENT_AWARD);
            },
          },
          {
            title: intl.formatMessage({
              id: 'permissionManager.tabTitle.advancementApprove',
            }),
            dataIndex: 'approve',
            key: 'approve',
            width: '9rem',
            render(_: unknown, data: RosterAdultMemberExtended) {
              return getCell(data.userOrgPositions, CelKey.ADVANCEMENT_APROVE);
            },
          },
          {
            title: intl.formatMessage({
              id: 'permissionManager.tabTitle.advancementEdit',
            }),
            dataIndex: 'edit',
            key: 'edit',
            width: '9rem',
            render(_: unknown, data: RosterAdultMemberExtended) {
              return getCell(data.userOrgPositions, CelKey.ADVANCEMENT_EDIT);
            },
          },
          ...getProgramSpecificAdvancementColumns(),
        ];
      case PanelTitle.CALENDAR:
        return [
          {
            title: intl.formatMessage({
              id: 'permissionManager.tabTitle.calendarCreate',
            }),
            dataIndex: 'create',
            key: 'create',
            width: '9rem',
            render(_: unknown, data: RosterAdultMemberExtended) {
              return getCell(data.userOrgPositions, CelKey.CALENDAR_CREATE);
            },
          },
        ];
      case PanelTitle.FINANCE:
        return [
          {
            title: intl.formatMessage({
              id: 'permissionManager.tabTitle.unitLogCreate',
            }),
            dataIndex: 'create',
            key: 'create',
            width: '9rem',
            render(_: unknown, data: RosterAdultMemberExtended) {
              return getCell(data.userOrgPositions, CelKey.FINANCE_CREATE);
            },
          },
          {
            title: intl.formatMessage({
              id: 'permissionManager.tabTitle.inidvLogCreate',
            }),
            dataIndex: 'indivCreate',
            key: 'indivCreate',
            width: '9rem',
            render(_: unknown, data: RosterAdultMemberExtended) {
              return getCell(data.userOrgPositions, CelKey.FINANCE_INDIVCREATE);
            },
          },
          {
            title: intl.formatMessage({
              id: 'permissionManager.tabTitle.poCreate',
            }),
            dataIndex: 'poCreate',
            key: 'poCreate',
            width: '9rem',
            render(_: unknown, data: RosterAdultMemberExtended) {
              return getCell(data.userOrgPositions, CelKey.FINANCE_POCREATE);
            },
          },
        ];
      case PanelTitle.LEADERSHIP:
        return [
          {
            title: intl.formatMessage({
              id: 'permissionManager.tabTitle.leaderAcceptance',
            }),
            dataIndex: 'acceptance',
            key: 'acceptance',
            width: '9rem',
            render(_: unknown, data: RosterAdultMemberExtended) {
              return getCell(
                data.userOrgPositions,
                CelKey.LEADERSHIP_ACCEPTANCE,
              );
            },
          },
          {
            title: intl.formatMessage({
              id: 'permissionManager.tabTitle.leaderApprove',
            }),
            dataIndex: 'leaderApprove',
            key: 'leaderApprove',
            width: '9rem',
            render(_: unknown, data: RosterAdultMemberExtended) {
              return getCell(data.userOrgPositions, CelKey.LEADERSHIP_APPROVE);
            },
          },
        ];
      case PanelTitle.MEMBERSHIP:
        return [
          {
            title: intl.formatMessage({
              id: 'permissionManager.tabTitle.inactiveYouthMembers',
            }),
            dataIndex: 'inactiveYouthMembers',
            key: 'inactiveYouthMembers',
            width: '9rem',
            render(_: unknown, data: RosterAdultMemberExtended) {
              return getCell(
                data.userOrgPositions,
                CelKey.MEMBERSHIP_INACTIVE_YOUTH,
              );
            },
          },
          {
            title: intl.formatMessage({
              id: 'permissionManager.tabTitle.inactiveAdultMembers',
            }),
            dataIndex: 'inactiveAdultMembers',
            key: 'inactiveAdultMembers',
            width: '9rem',
            render(_: unknown, data: RosterAdultMemberExtended) {
              return getCell(
                data.userOrgPositions,
                CelKey.MEMBERSHIP_INACTIVE_ADULT,
              );
            },
          },
        ];
      case PanelTitle.MESSAGING:
        return [
          {
            title: intl.formatMessage({
              id: 'permissionManager.tabTitle.messageCreate',
            }),
            dataIndex: 'messageCreate',
            key: 'messageCreate',
            width: '9rem',
            render(_: unknown, data: RosterAdultMemberExtended) {
              return getCell(data.userOrgPositions, CelKey.MESSAGING_CREATE);
            },
          },
          {
            title: intl.formatMessage({
              id: 'permissionManager.tabTitle.groupCreate',
            }),
            dataIndex: 'groupCreate',
            key: 'groupCreate',
            width: '9rem',
            render(_: unknown, data: RosterAdultMemberExtended) {
              return getCell(
                data.userOrgPositions,
                CelKey.MESSAGING_GROPU_CREATE,
              );
            },
          },
        ];
      case PanelTitle.UNIT:
        return [
          {
            title: intl.formatMessage({
              id: 'permissionManager.tabTitle.profileEdit',
            }),
            dataIndex: 'profileEdit',
            key: 'profileEdit',
            width: '9rem',
            render(_: unknown, data: RosterAdultMemberExtended) {
              return getCell(data.userOrgPositions, CelKey.UNIT_PROFILE_EDIT);
            },
          },
          {
            title: intl.formatMessage({
              id: 'permissionManager.tabTitle.unitEdit',
            }),
            dataIndex: 'unitEdit',
            key: 'unitEdit',
            width: '9rem',
            render(_: unknown, data: RosterAdultMemberExtended) {
              return getCell(data.userOrgPositions, CelKey.UNIT_EDIT);
            },
          },
          {
            title: intl.formatMessage({
              id: 'permissionManager.tabTitle.reports',
            }),
            dataIndex: 'reports',
            key: 'reports',
            width: '9rem',
            render(_: unknown, data: RosterAdultMemberExtended) {
              return getCell(data.userOrgPositions, CelKey.UNIT_REPORTS);
            },
          },
          ...getProgramSpecificUnitColumns(),
        ];
      default:
        return [];
    }
  };

  const columns = [
    {
      title: intl.formatMessage({
        id: 'shared.name',
      }),
      dataIndex: 'personFullName',
      key: 'personFullName',
      width: '10rem',
      render(personFullName: string) {
        return (
          <div className={styles.nameContainer}>
            <PersonAvatar isAdult bordered={false} className={styles.avatar} />
            <S colored size="4">
              {personFullName}
            </S>
          </div>
        );
      },
    },
    {
      title: intl.formatMessage({
        id: 'permissionManager.tabTitle.role',
      }),
      dataIndex: 'userOrgPositions',
      key: 'userOrgPositions',
      width: '10rem',
      render(
        userOrgPositions: OrgPositionDetail[],
        item: RosterAdultMemberExtended,
      ) {
        return (
          <>
            {userOrgPositions.map(pos => (
              <div key={`${item.memberId}-${pos.PositionId}`}>
                <S size="5">{pos.positionLong}</S>
              </div>
            ))}
          </>
        );
      },
    },
    {
      title: '',
      dataIndex: 'specifity',
      key: 'specifity',
      width: '9rem',
    },
    ...getColumns(panelType),
  ];

  const getPersonRowKey = (rowData: RosterAdultMemberExtended) => {
    const { userId } = rowData;
    return `${userId}`;
  };

  const renderTitle = () => (
    <div className={styles.tableTitle}>
      <Button
        type="ghost"
        noBorder
        size="small"
        icon={<MdAddCircle />}
        uppercase={false}
        className={styles.tableTitleBtn}
      >
        <FormattedMessage id="permissionManager.permissionTab.selectRecommended" />
      </Button>
      <Button
        type="ghost"
        noBorder
        size="small"
        icon={<MdOutlineRemoveCircle />}
        uppercase={false}
        className={styles.tableTitleBtn}
      >
        <FormattedMessage id="permissionManager.permissionTab.removeRecommended" />
      </Button>
      <div className={styles.searchInputContainer}>
        <Input
          size="large"
          rounded
          addonAfter={<MdSearch />}
          className={styles.searchInput}
          placeholder="Search by Name"
          value={searchTxt}
          onChange={e => setSearchTxt(e.target.value)}
        />
      </div>
    </div>
  );

  return (
    <Table
      rowKey={getPersonRowKey}
      loading={loading}
      title={renderTitle}
      columns={columns}
      dataSource={filteredTableData}
      size="middle"
      scroll={{ y: 'calc(50vh - 80px)' }}
      pagination={false}
    />
  );
};
